// `?signup=1` is what fastn.ai's "Start free" link carries: the visitor should
// see registration, not "Welcome back / Sign in".
//
// It must still check for a live SSO session first. Keycloak's registration
// endpoint expires the identity cookie before it renders, so sending a
// signed-in customer there signs them out of every fastn tab. `check-sso`
// answers that (prompt=none round trip), and only a confirmed "no session"
// goes on to register().

import type { KeycloakInitOptions } from "keycloak-js";

const SIGNUP_PARAM = "signup";

export function wantsSignup(search: string): boolean {
  return new URLSearchParams(search).get(SIGNUP_PARAM) === "1";
}

/** `href` minus `signup` and any fragment; every other param (UTMs, landing_page) is kept. */
export function withoutSignupParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(SIGNUP_PARAM);
  url.hash = "";
  return url.toString();
}

export function initOptionsFor(signup: boolean): KeycloakInitOptions {
  return {
    onLoad: signup ? "check-sso" : "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false,
  };
}

export interface SignupEntryDeps {
  register: (options: { redirectUri: string }) => Promise<void>;
  replaceUrl: (url: string) => void;
}

/**
 * After init: a signup visit with no session goes to registration; one that
 * turned out to be signed in just drops `signup` from the address bar.
 */
export async function continueSignupEntry(
  authenticated: boolean,
  signup: boolean,
  href: string,
  deps: SignupEntryDeps,
): Promise<void> {
  if (!signup) return;
  const clean = withoutSignupParam(href);
  if (authenticated) {
    deps.replaceUrl(clean);
    return;
  }
  await deps.register({ redirectUri: clean });
}
